import { jwtDecode } from 'jwt-decode';
import  authApiService from '@/app/services/auth/authServices';
import { DecodedToken } from '@/shared/types/auth';
import { useAclStore } from '@/app/stores/aclStore';
import { getEnvVar } from '@/app/utils/env';

/**
 * Get the session identifier from localStorage
 * @returns The session identifier or null if not found
 */
export const getSessionIdentifier = (): string | null => {
  return localStorage.getItem('sessionIdentifier');
};

/**
 * Get the decoded token from localStorage
 * @returns The decoded token or null if not found
 */
export const getDecodedToken = (): DecodedToken | null => {
  try {
    const tokenString = localStorage.getItem('token') || '';
    return tokenString ? JSON.parse(tokenString) : null;
  } catch (error) {
    console.error('Error parsing decoded token:', error);
    return null;
  }
};

/**
 * Check if token is expired locally
 */
export const isTokenExpired = (token: DecodedToken): boolean => {
  const currentTime = Math.floor(Date.now() / 1000);
  const bufferTime = 60; // 1 minute buffer
  return token.exp <= (currentTime + bufferTime);
};

/**
 * Login with JWT token - validates and stores authentication data
 */
export const loginWithToken = async (accessToken: string): Promise<{
  decodedToken: DecodedToken;
  sessionIdentifier: string;
}> => {
  try {
    // Decode the JWT token
    const decodedToken: DecodedToken = jwtDecode(accessToken);
    
    // Validate required fields
    if (!decodedToken.SessionIdentifier || !decodedToken.UserID || !decodedToken.EmailAddress) {
      throw new Error('Invalid token structure');
    }

    // Validate the session identifier with the backend
    const validationResponse = await authApiService.validateSession(decodedToken.SessionIdentifier);

    if (!validationResponse.status || !validationResponse.sessionid.isExists) {
      throw new Error('Invalid session identifier');
    }

    // Store authentication data
    localStorage.setItem('token', JSON.stringify(decodedToken));
    localStorage.setItem('sessionIdentifier', decodedToken.SessionIdentifier);

    return {
      decodedToken,
      sessionIdentifier: decodedToken.SessionIdentifier
    };
  } catch (error) {
    // Clean up any stored data on failure
    clearAuthData();
    throw error;
  }
};

/**
 * Check if the current session is valid (not expired)
 * @returns true if the session is valid, false otherwise
 */
export const isSessionValid = async (): Promise<boolean> => {
  const token = getDecodedToken();
  
  if (!token) return false;
  
  // First check if token is expired locally (optimization)
  if (isTokenExpired(token)) {
    return false;
  }
  
  // Then validate with backend
  try {
    const sessionIdentifier = token.SessionIdentifier;
    if (!sessionIdentifier) return false;
    
    // Import dynamically to avoid circular dependencies
    const authService = (await import('@/app/services/auth/authServices')).default;
    const response = await authService.validateSession(sessionIdentifier);
    
    return response.status && response.sessionid.isExists;
  } catch (error) {
    console.error('Error validating session with backend:', error);
    return false;
  }
};

/**
 * Build a Basic Auth header value from env vars, or undefined when not configured.
 * Uses VITE_API_BASIC_AUTH_USERNAME and VITE_API_BASIC_AUTH_PASSWORD.
 */
export function buildBasicAuthHeader(): string | undefined {
  const username = getEnvVar('VITE_API_BASIC_AUTH_USERNAME');
  const password = getEnvVar('VITE_API_BASIC_AUTH_PASSWORD');
  if (!username || !password) return undefined;
  return `Basic ${btoa(`${username}:${password}`)}`;
}

/**
 * External MyRCL login URL used when the user has no access token or session.
 */
export function getMyrclLoginUrl(): string {
  return getEnvVar('VITE_MYRCL_LOGIN_URL', false, '');
}

/**
 * Clear all authentication data from localStorage
 */
export const clearAuthData = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('sessionIdentifier');
  localStorage.removeItem('user');
  
  // Clear Zustand ACL store data
  useAclStore.getState().clear();
};

/**
 * Check if user is authenticated
 * @returns true if user is authenticated with valid session
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const sessionIdentifier = getSessionIdentifier();
  
  if (!sessionIdentifier) return false;
  
  return await isSessionValid();
};
